import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EXT_PROMO_KEY = "stp_ext_promo_dismissed_v1";

function ExtensionPromo() {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(EXT_PROMO_KEY) === "1"
  );
  if (dismissed) return null;
  const close = () => { localStorage.setItem(EXT_PROMO_KEY, "1"); setDismissed(true); };
  return (
    <div
      className="ff-card relative overflow-hidden p-5 md:p-6 bg-gradient-to-br from-[#E8F0E9] to-[#FFF6E3]"
      data-testid="extension-promo-banner"
    >
      <button
        onClick={close}
        aria-label="Dismiss"
        data-testid="extension-promo-dismiss"
        className="absolute top-3 right-3 w-7 h-7 rounded-full text-[#5C6B61] hover:text-[#2A332C] hover:bg-white/60 transition flex items-center justify-center text-sm"
      >
        ✕
      </button>
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-2xl flex-none">🥗</span>
        <div className="flex-1 min-w-0 pr-6">
          <div className="ff-label text-[#3B5A3E]">New · save ~20 min / shop</div>
          <h3 className="text-lg md:text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Send this basket to your supermarket
          </h3>
          <p className="text-sm text-[#5C6B61] mt-1.5 leading-relaxed">
            We open each item in <strong>Tesco</strong>, <strong>Sainsbury&apos;s</strong>, <strong>ASDA</strong>, <strong>Morrisons</strong>, <strong>Waitrose</strong>, <strong>Aldi</strong> or <strong>Lidl</strong> for you — you just tap <strong>Add</strong>. No typing, nothing missed.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Link
              to="/extension"
              className="ff-btn-primary text-sm"
              data-testid="extension-promo-cta"
            >
              Set it up — it&apos;s free →
            </Link>
            <Link
              to="/extension#mobile"
              className="text-xs font-medium text-[#3B5A3E] hover:underline"
              data-testid="extension-promo-mobile-link"
            >
              📱 On phone?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function ShoppingLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderingId, setReorderingId] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // Lazy weekly-reorder check — auto-clone last basket if it's the user's day.
        const reorderRes = await api.post("/check-reorder").catch(() => null);
        const { data } = await api.get("/shopping-lists");
        setLists(data);
        if (reorderRes?.data?.auto_reordered) {
          const newList = reorderRes.data.list;
          toast.success(`🔁 Your weekly basket is ready — ${newList.name}`, {
            duration: 12000,
            action: {
              label: "Snooze this week",
              onClick: async () => {
                try {
                  await api.post("/snooze-week", { list_id: newList.list_id });
                  setLists((prev) => prev.filter((x) => x.list_id !== newList.list_id));
                  toast("Snoozed — we'll resume next week 💤");
                } catch {
                  toast.error("Couldn't snooze");
                }
              },
            },
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const remove = async (id) => {
    if (!confirm("Delete this list?")) return;
    await api.delete(`/shopping-lists/${id}`);
    setLists((l) => l.filter(x => x.list_id !== id));
  };

  const reorder = async (listId) => {
    setReorderingId(listId);
    try {
      const { data } = await api.post(`/shopping-lists/${listId}/reorder`);
      toast.success("Fresh basket ready");
      nav(`/app/shopping/${data.list_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reorder");
    } finally {
      setReorderingId(null);
    }
  };

  const last = lists[0];

  return (
    <div className="space-y-8 ff-fade-in">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ff-label">Your basket</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Shopping lists
          </h1>
          <p className="text-[#5C6B61] mt-2">Pick a list and we&apos;ll line up your ingredients with each UK supermarket.</p>
        </div>
        {last && (
          <button
            onClick={() => reorder(last.list_id)}
            disabled={reorderingId === last.list_id}
            data-testid="reorder-last-btn"
            className="ff-btn-primary self-start disabled:opacity-60"
          >
            {reorderingId === last.list_id ? "Duplicating…" : `🔁 Reorder last basket (${last.items.length} items)`}
          </button>
        )}
      </header>

      <ExtensionPromo />

      {loading ? (
        <div className="text-[#5C6B61]">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="ff-card p-10 text-center">
          <img
            src="https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600"
            className="w-40 h-40 object-cover rounded-3xl mx-auto mb-6" alt=""
          />
          <h3 className="text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>No lists yet</h3>
          <p className="text-[#5C6B61] mt-2 max-w-md mx-auto">
            Open the Recipes tab, tick the dishes you want this week, then hit &ldquo;Build shopping list&rdquo;.
          </p>
          <Link to="/app/recipes" className="ff-btn-primary mt-6 inline-block">Browse recipes</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {lists.map((l) => (
            <div key={l.list_id} className="ff-card ff-card-hover p-6" data-testid="shopping-list-card">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/app/shopping/${l.list_id}`} className="flex-1">
                  <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{l.name}</h3>
                  <p className="text-sm text-[#5C6B61] mt-1">
                    {l.items.length} items · {new Date(l.created_at).toLocaleDateString("en-GB")}
                  </p>
                </Link>
                <button onClick={() => remove(l.list_id)} className="text-xs text-[#8B9690] hover:text-[#C36952]">Remove</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {l.items.slice(0, 6).map((it, i) => (
                  <span key={i} className="ff-chip">{it.name}</span>
                ))}
                {l.items.length > 6 && <span className="ff-chip">+{l.items.length - 6} more</span>}
              </div>
              <div className="mt-5 flex items-center justify-between gap-2">
                <Link to={`/app/shopping/${l.list_id}`} className="text-sm font-medium text-[#3B5A3E] hover:underline underline-offset-4">
                  Open list →
                </Link>
                <button
                  onClick={() => reorder(l.list_id)}
                  disabled={reorderingId === l.list_id}
                  data-testid="reorder-list-btn"
                  className="text-xs font-medium rounded-full px-3 py-1.5 bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA] transition disabled:opacity-60"
                  title="Make a fresh copy of this basket"
                >
                  {reorderingId === l.list_id ? "…" : "🔁 Reorder"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
